import React from 'react';
import { Users, Target, Lightbulb, TrendingUp, Shield, Zap, ArrowRight } from 'lucide-react';

const engagements = [
  {
    icon: Target,
    title: 'Strategy & Roadmap',
    description: 'Define your digital transformation roadmap with a clear, business-aligned plan that your teams can actually execute.',
    details: ['Technology & maturity assessment', 'Prioritised transformation roadmap', 'Tool stack evaluation', 'ROI-focused planning'],
    accentColor: '#00d4ff',
  },
  {
    icon: Lightbulb,
    title: 'Process Optimisation',
    description: 'Identify and eliminate bottlenecks across your engineering and delivery processes.',
    details: ['CI/CD pipeline optimisation', 'Test automation strategy', 'Shift-left implementation', 'Flaky test remediation'],
    accentColor: '#10b981',
  },
  {
    icon: Users,
    title: 'Team Building & Coaching',
    description: 'Build high-performing engineering teams with the right structure, skills, and culture.',
    details: ['Team structure & hiring strategy', 'Technical skills training', 'Agile / DevOps transformation', 'Leadership coaching'],
    accentColor: '#a855f7',
  },
  {
    icon: TrendingUp,
    title: 'Metrics & Business Alignment',
    description: 'Connect engineering quality to business outcomes that executives understand and act on.',
    details: ['KPI definition & tracking', 'Executive dashboard design', 'Quality-to-revenue correlation', 'ROI frameworks'],
    accentColor: '#f59e0b',
  },
  {
    icon: Shield,
    title: 'Technical Debt Management',
    description: 'Prioritise, address, and prevent the technical debt that slows your team and your business.',
    details: ['Debt assessment & scoring', 'Remediation strategy', 'Prevention frameworks', 'Long-term sustainability'],
    accentColor: '#06b6d4',
  },
  {
    icon: Zap,
    title: 'Crisis Resolution',
    description: 'Rapid intervention when quality issues are threatening your production systems or release timelines.',
    details: ['Production incident analysis', 'Emergency process improvements', 'Team stabilisation', 'Preventive implementation'],
    accentColor: '#ef4444',
  },
];

const Consultancy = () => (
  <section id="consultancy" className="relative py-24 overflow-hidden">
    <div className="absolute inset-0 circuit-pattern opacity-15" />
    <div className="absolute inset-0"
      style={{ background: 'radial-gradient(circle at 70% 30%, rgba(0,102,255,0.04), transparent 50%)' }} />

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="max-w-2xl mb-16">
        <div className="inline-flex items-center space-x-2 rounded-full px-4 py-1.5 mb-6"
          style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)' }}>
          <Users className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-xs font-medium text-cyan-400 tracking-wide uppercase">How We Engage</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
          Hands-on, not<br />
          <span className="gradient-text">just strategy</span>
        </h2>
        <p className="text-gray-400 leading-relaxed">
          We don't write PowerPoint decks and leave. We work alongside your team — writing code, reviewing architectures, 
          and implementing solutions. 15 years of Fintech, Payments, and Healthcare experience behind every engagement.
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
        {engagements.map((eng, i) => {
          const Icon = eng.icon;
          return (
            <div key={i}
              className="group rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = `${eng.accentColor}30`}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                style={{ background: `${eng.accentColor}15`, border: `1px solid ${eng.accentColor}30` }}>
                <Icon className="w-5 h-5" style={{ color: eng.accentColor }} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{eng.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-4">{eng.description}</p>
              <ul className="space-y-1.5">
                {eng.details.map((d, j) => (
                  <li key={j} className="flex items-center space-x-2 text-xs text-gray-500">
                    <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: eng.accentColor }} />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div className="rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
        }}>
        <div>
          <h3 className="text-xl font-bold text-white mb-1">Ready to solve your challenges?</h3>
          <p className="text-sm text-gray-400">Let's talk about your specific pain points — no commitment required.</p>
        </div>
        <a href="#contact"
          onClick={e => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
          className="flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold text-white text-sm whitespace-nowrap flex-shrink-0 transition-all"
          style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}>
          <span>Schedule a Call</span>
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
);

export default Consultancy;
